import { Trans, useLingui } from "@lingui/react/macro";
import React from "react";
import * as styles from "./ShipFacilityModal.module.scss";
import { SlideshowItem } from "components/Slideshow/Slideshow";
import { BlockContent } from "components/BlockContent";
import {
  SanityShipSections,
  ShipSuiteModalData_SuiteFragment
} from "types/graphql-types";
import ReadMore from "components/ReadMore/ReadMore";
import {
  ButtonsContainer,
  isSanityImagePresent,
  ResponsiveSanityImage,
  SanityImageRatio,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  Price
} from "@silversea-ssc/ui-components-react";
import { isPresent } from "src/utils/checks";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import { SUITE_FEATURE_GROUPINGS } from "@silversea-ssc/web-suite-features";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import ECommerceButtons from "components/ECommerceButtons";
import { isSuitePriceWaitlist } from "src/utils/suites";
import { LinguiI18n } from "src/utils/localization/lingui-helpers";
import PriceList from "components/Prices/PriceList/PriceList";
const ResponsiveSlideshow = React.lazy(
  () => import("components/Slideshow/ResponsiveSlideshow")
);
interface SuiteModalContentProps {
  suite: ShipSuiteModalData_SuiteFragment;
  prices?: Price[];
  raqCTAUrl?: string;
  ecommerceStatus?: ECommerceStatus;
  deckPlanImages?: SlideshowItem[];
}

const featureText = (i18n: LinguiI18n, feature) => {
  if (feature.type === "boolean") return i18n._(feature.key);
  return i18n._(feature.value);
};

const featureCaveat = (feature) =>
  feature.caveat?.localized ? ` (${feature.caveat.localized})` : "";

const mapFeatureGroupings = (i18n: LinguiI18n, comparableFeatures) =>
  SUITE_FEATURE_GROUPINGS.map((grouping) => ({
    title: i18n._(grouping.key),
    features: grouping.features
      .map((feature) => ({
        ...feature,
        value: (comparableFeatures || {})[feature.key],
        caveat: (comparableFeatures || {})[feature.key + "_caveat"]
      }))
      .filter((x) => x.value)
      .map(
        (feature) => `${featureText(i18n, feature)}${featureCaveat(feature)}`
      )
  }));

const getSectionKeys = (sections: SanityShipSections): string[] => {
  const result: string[] = [];
  if (sections.aft) result.push("shipSection_aft");
  if (sections.forward) result.push("shipSection_forward");
  if (sections.midShip) result.push("shipSection_midShip");
  if (sections.port) result.push("shipSection_port");
  if (sections.starboard) result.push("shipSection_starboard");
  return result;
};

const SuiteModalContent: React.FC<
  React.PropsWithChildren<SuiteModalContentProps>
> = ({ suite, prices = null, raqCTAUrl, ecommerceStatus, deckPlanImages }) => {
  const { t, i18n } = useLingui();
  const title = suite?.card?.title?.localized;
  const description = suite?.description?.localized;
  const images = suite?.images?.filter(isPresent);
  const groupings = mapFeatureGroupings(i18n, suite.comparableFeatures);
  const geoLocation = useGeolocation();
  const firstConfiguration = suite?.configurations?.[0];
  const useFeet = geoLocation.unit === "feet";

  return (
    <div className={`${styles.container}`}>
      <div className={styles.leftSide}>
        <Tabs data-content-name="tabs">
          <TabList wrapperClassName={styles.tabList} spacingTop>
            <Tab noWrap underline data-content-piece={"suite-overview"}>
              <Trans>Suite Overview</Trans>
            </Tab>
            <Tab noWrap underline data-content-piece={"suite-plan"}>
              <Trans>Suite Plan</Trans>
            </Tab>
            <Tab noWrap underline data-content-piece={"suite-features"}>
              <Trans>Suite Features</Trans>
            </Tab>
            {deckPlanImages && deckPlanImages.length > 0 && (
              <Tab noWrap underline data-content-piece={"suite-location"}>
                <Trans>Suite Location</Trans>
              </Tab>
            )}
          </TabList>

          <TabPanel
            spacingTop
            className={styles.tabPanel}
            data-content-name="tab_content_overview"
          >
            {images && (
              <React.Suspense>
                <ResponsiveSlideshow
                  items={images}
                  variant="gray"
                  fullscreen={false}
                  hideCaption={false}
                  dataContentName="slideshow"
                />
              </React.Suspense>
            )}
          </TabPanel>
          <TabPanel
            spacingTop
            className={styles.tabPanel}
            data-content-name="tab_content_plan"
          >
            {isSanityImagePresent(firstConfiguration?.plan) && (
              <SanityImageRatio ratio="auto" image={firstConfiguration?.plan}>
                <ResponsiveSanityImage
                  image={firstConfiguration?.plan}
                  className={styles.suitePlan}
                  defaultWidth={600}
                  defaultHeight={500}
                  fit="original"
                  alt="Ship Plan"
                />
              </SanityImageRatio>
            )}
          </TabPanel>
          <TabPanel
            spacingTop
            className={styles.tabPanel}
            data-content-name="tab_content_features"
          >
            <ReadMore
              height="6.5em"
              backgroundColor="white"
              openLabel={<Trans>Expand Info</Trans>}
              closeLabel={<Trans>Reduce Info</Trans>}
            >
              <div className={styles.suiteFeatures}>
                <div className={styles.group}>
                  <h3>{t`suite_essentials`}</h3>
                  <ul>
                    <li>
                      <span>
                        {t`suite_deckS`}: {suite.deck?.join(", ")}
                      </span>
                    </li>
                    {suite.shipSections && (
                      <li>
                        <span>
                          {t`suite_shipSection`}:{" "}
                          {getSectionKeys(suite.shipSections)
                            .map((key) => i18n._(key))
                            .join(", ")}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                {groupings.map((grouping) => {
                  return (
                    <div key={grouping.title} className={styles.group}>
                      <h3>{grouping.title}</h3>
                      <ul>
                        {grouping.features.map((feature) => (
                          <li key={feature}>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </ReadMore>
          </TabPanel>
          {deckPlanImages && deckPlanImages.length > 0 && (
            <TabPanel
              spacingTop
              className={styles.tabPanel}
              data-content-name="tab_content_location"
            >
              {suite.color && (
                <div className={styles.legend}>
                  <span
                    className={styles.colorLegend}
                    style={{ backgroundColor: `#${suite.color}` }}
                  >
                    {" "}
                  </span>
                  {title}
                </div>
              )}
              <div className={styles.deckPlanSlider}>
                <React.Suspense>
                  <ResponsiveSlideshow
                    imageFit="contain"
                    items={deckPlanImages}
                    variant="gray"
                    fullscreen={false}
                    hideCaption={false}
                    fluidHeight
                    dataContentName="suite_location_slideshow"
                  />
                </React.Suspense>
              </div>
            </TabPanel>
          )}
        </Tabs>
      </div>
      <div className={styles.rightSide}>
        <h3>{title}</h3>
        <BlockContent value={description} />
        {suite?.configurations?.map((configuration) => {
          const shortSizeDescription = useFeet
            ? configuration?.shortSizeDescriptionInSquareFeet?.localized
            : configuration?.shortSizeDescriptionInSquareMetres?.localized;

          const verandaSizeDescription = useFeet
            ? configuration?.verandaSizeInSquareFeet?.localized
            : configuration?.verandaSizeInSquareMetres?.localized;

          return (
            <div className={styles.bedroomsInfo} key={configuration?.code}>
              <p>
                {configuration?.name?.localized}: {shortSizeDescription}{" "}
                {verandaSizeDescription ? `(${verandaSizeDescription})` : ""}
              </p>
              <BlockContent
                value={configuration?.configurationInformation?.localized}
              />
            </div>
          );
        })}

        {prices && (
          <div className={styles.raqContainer}>
            <PriceList
              prices={prices}
              topPricesDisclaimer={t`Prices per guest start from`}
              chipBackgroundColor="neutral05"
            />

            <ButtonsContainer
              mobileFullSize
              align="left"
              spacingTop="regular"
              gap="compact"
            >
              <ECommerceButtons
                ecommerceStatus={
                  isSuitePriceWaitlist(prices)
                    ? { type: "disabled" }
                    : ecommerceStatus
                }
                size="regular"
                raqUrl={raqCTAUrl}
              />
            </ButtonsContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuiteModalContent;
